import { Drawer, Flex, Image } from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "../../../../../router/LocaleLink";
import { MenuIcon } from "../../../../../assets/icons";
import { Paths, PathGenerators } from "../../../../../router/paths";
import { useAuthEntryPath } from "../../../../../router/useAuthEntryPath";
import { Menu } from "../Menu";
import { getMenuItemsForRole } from "../menuItems";
import { useAuth } from "../../../../../api/auth/useAuth";
import { useShouldHideSubscriptions } from "../../../../../hooks/useShouldHideSubscriptions";
import { ProfileMenu } from "../../Header/ProfileMenu";
import { StockSearch } from "../../../../components/StockSearch";
import { Button } from "../../../../components/CustomAntdComponents/Button";
import { NotificationsMenu } from "../../Header/NotificationsMenu";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../../../../../components/LanguageSwitcher";
import { trackSidebarSectionClicked } from "../../../../../analytics/amplitudeTracking";
import {
  getSidebarMenuItemsWithMeta,
} from "../../../../../analytics/sidebarMenuTracking";
import type { MenuItem } from "../Menu";
import styles from "./MobileSidebar.module.css";

export const MobileSidebar = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const loginPath = useAuthEntryPath(Paths.LOGIN);
  const signUpPath = useAuthEntryPath(Paths.SIGN_UP);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const hideSubscriptions = useShouldHideSubscriptions();
  const roleName = user?.role?.name;
  const roleMenuGroups = getMenuItemsForRole(roleName, {
    hideSubscriptions,
    hideMobileOnly: true,
  });
  const sidebarMenuMetaByKey = new Map(
    getSidebarMenuItemsWithMeta(roleName).map((entry) => [
      entry.item.key,
      entry,
    ]),
  );

  const handleSidebarItemClick = (
    item: MenuItem,
    meta: { section_position: number; section_category: string },
  ) => {
    const resolved = sidebarMenuMetaByKey.get(item.key);
    trackSidebarSectionClicked({
      section_name: item.labelKey,
      section_position: resolved?.section_position ?? meta.section_position,
      section_category: resolved?.section_category ?? meta.section_category,
    });
  };

  const handleStockSelect = (symbol: string) => {
    navigate(PathGenerators.SYMBOL(symbol));
  };

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <div className={styles.mobileShell}>
      <Flex
        justify="space-between"
        align="center"
        className={styles.header}
        data-sticky-header
      >
        <Link to={Paths.HOME}>
          <Image
            preview={false}
            src="/Logomark.webp"
            alt="logo"
            width={48}
            height={48}
          />
        </Link>
        <div className={styles.searchContainer}>
          <StockSearch
            width="100%"
            placeholder={t("layout.searchStocks")}
            actionLabel={t("layout.search")}
            onSelect={handleStockSelect}
          />
        </div>
        <Flex gap={8} align="center">
          <LanguageSwitcher />
          <Button
            style={{ minWidth: 44, minHeight: 44 }}
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon />
          </Button>
          {user ? <NotificationsMenu /> : null}
        </Flex>
      </Flex>

      <Drawer
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        destroyOnClose
        width={300}
        zIndex={60}
        maskClosable
        styles={{
          body: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
          header: {
            paddingTop: "calc(16px + env(safe-area-inset-top))",
          },
        }}
      >
        <div className={styles.drawerBodyInner}>
          {user ? (
            <div className={styles.drawerUserRow}>
              <span className={styles.drawerProfileLabel}>
                {t("layout.profileLabel")}
              </span>
              <ProfileMenu />
            </div>
          ) : null}
          {roleMenuGroups.map((item, index) => (
            <Menu
              key={item[0]?.key || index}
              items={item}
              onItemClick={handleSidebarItemClick}
            />
          ))}
          {!user && (
            <Flex gap={"8px"}>
              <Link to={loginPath}>
                <Button type="default" style={{ minHeight: 44 }}>
                  {t("layout.login")}
                </Button>
              </Link>
              <Link to={signUpPath}>
                <Button type="primary" style={{ minHeight: 44 }}>
                  {t("layout.signUp")}
                </Button>
              </Link>
            </Flex>
          )}
        </div>
      </Drawer>
    </div>
  );
};
